<template>
  <transition name="fade">
    <div v-if="shouldShow">
      <AkHeading2
        v-if="label"
        class="mx-auto ds-flex ds-font-bold ds-text-2xl ds-mb-4"
        data-testid="recommendations-title"
      >
        {{ label }}
      </AkHeading2>
      <RecommendationsCarousel
        ref="carousel"
        class="recommendations-widget"
        :title="label"
        :algorithm="algorithm"
        :max-items-override="carouselMaxItems"
        :item-spacing-override="carouselItemSpacing"
      >
        <template v-if="isProductsCarousel">
          <IntersectionObserver
            v-for="(item, index) in productItems"
            :id="index + 1"
            :key="index"
            :threshold="1"
            @change="productObserverChanged(arguments, item, index)"
          >
            <CardProductRecommendation
              v-if="$isEnabled('RET-3436')"
              :product="item"
              :position="index"
              :page-number="0"
              :is-user-authenticated="isUserAuthenticated"
              :can-get-wholesale-price="canGetWholesalePrice"
              :is-external-link="isExternalLink"
              with-drawer
              hide-volume-discount
              hide-estimated-delivery
              @clicked="productClicked(item, index)"
              @clicked-drawer="productClicked(item, index)"
              @brand-clicked="handleBrandClicked(item, index)"
              @added-to-cart="handleCardProductAddedToCart($event, item, index)"
              @like-clicked="handleLikeClicked(item, index)"
            />
            <ProductSnippet
              v-else
              :key="item.id"
              :item="item"
              full-width
              with-brand-link
              :with-best-seller-badge="withBestsellerBadge"
              :with-checkbox="withCheckbox"
              :additional-like-tracking-data="getLikeTrackingData(index, item)"
              :parent-section-id="sectionId"
              @clicked="productClicked(item, index)"
              @variant-added-to-cart="trackQuickAddToCartProductAdded"
            />
          </IntersectionObserver>
        </template>
        <template v-else>
          <IntersectionObserver
            v-for="(item, index) in brandItems"
            :id="index + 1"
            :key="index"
            :threshold="1"
            @change="productObserverChanged(arguments, item, index)"
          >
            <BrandSnippet
              :brand="item"
              full-width
              @clicked="brandClicked(item, index)"
            />
          </IntersectionObserver>
        </template>
      </RecommendationsCarousel>
    </div>
  </transition>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import ProductSnippet from '@monolith/legacy/components/product/product-snippet-async.vue';
import BrandSnippet from '@monolith/legacy/components/brand/brand-snippet-async.vue';
import RecommendationsCarousel from '../recommendations-carousel/recommendations-carousel.vue';
import {
  AdditionalLikeTrackingData,
  EventName,
  ImpressionElement,
  RecommendationAlgoType,
  snippetType,
  TrackClickItem,
} from '@monolith/legacy/types/analytics/recommendation-tracking';
import { mapGetters } from 'vuex';
import Analytics from '@monolith/legacy/services/analytics';
import { SectionCreatedEvent } from '@monolith/legacy/services/analytics/events/recomendations/section-created-event';
import IntersectionObserver from '@monolith/legacy/components/global/interception-observer.vue';
import { ViewTileEvent } from '@monolith/legacy/services/analytics/events/recomendations/view-tile-event';
import { ClickEvent } from '@monolith/legacy/services/analytics/events/recomendations/click-event';
import { ProductQuickAddToCartEvent } from '@monolith/legacy/services/analytics/events/product-quick-added-to-cart.event';
import { createProductProperty, type ProductProperty } from '@monolith/legacy/services/analytics/properties/product-property';
import { createBrandProperty, type BrandProperty } from '@monolith/legacy/services/analytics/properties/brand-property';
import { isEnabled } from '@monolith/legacy/services/features';
import { trackMergeFn } from './utils';
import Product, { isProduct } from '@monolith/legacy/types/product';
import { Brand as ApiBrand } from '@monolith/legacy/types/api/brand';
import { oneOf } from '@core/utilities/propValidators';
import { Recommendations } from '../../types';
import { getSectionId, IdAlgo } from '@bc/shared';
import { CardProductRecommendation } from '@bc/discovery/feature/cards';

const MIN_NUM_OF_RECOS_TO_DISPLAY = 4;
enum ItemTypes {
  PRODUCT = 'product',
  BRAND = 'brand',
}
enum DisplayTypeEnum {
  CAROUSEL = 'carousel',
  GRID = 'grid',
}

type DisplayType = DisplayTypeEnum.CAROUSEL | DisplayTypeEnum.GRID;

export default defineComponent({
  name: 'RecommendationsWidget',
  components: {
    IntersectionObserver,
    ProductSnippet,
    BrandSnippet,
    RecommendationsCarousel,
    CardProductRecommendation,
  },
  props: {
    items: {
      type: Array as PropType<Recommendations['products'] | Recommendations['brands']>,
      required: true,
    },
    label: {
      type: String,
      default: null,
    },
    widgetProposition: {
      type: String,
      required: true,
    },
    algorithm: {
      type: Number as PropType<RecommendationAlgoType>,
      required: true,
    },
    itemNumber: {
      type: Number,
      default: null,
    },
    minNumberRecordsToShow: {
      type: Number,
      default: MIN_NUM_OF_RECOS_TO_DISPLAY,
    },
    displayOrder: { type: Number, default: 1 },
    shouldTrackTileViews: {
      type: Boolean,
      default: () => isEnabled('track-tile-views'),
    },
    withCheckbox: {
      type: Boolean,
      default: false,
    },
    displayType: {
      type: String as PropType<DisplayType>,
      default: DisplayTypeEnum.CAROUSEL,
      validator: oneOf(Object.values(DisplayTypeEnum)),
    },
    withBestsellerBadge: {
      type: Boolean,
      default: false,
    },
    isExternalLink: {
      type: Boolean,
      default: false,
    },
    carouselMaxItems: {
      type: Number,
      default: undefined,
    },
    carouselItemSpacing: {
      type: Number,
      default: undefined,
    },
  },
  emits: ['impression-id'],
  computed: {
    ...mapGetters(['user', 'isUserAuthenticated', 'canGetWholesalePrice']),
    carousel(): HTMLElement {
      return this.$refs.carousel as HTMLElement;
    },
    shouldShow(): boolean {
      return this.items.length >= this.minNumberRecordsToShow;
    },
    positionInPage(): number {
      return this.displayOrder - 1;
    },
    sectionId(): string {
      return getSectionId({
        valueProposition: this.widgetProposition,
        sectionType: ImpressionElement.recoWidget,
      });
    },
    isProductsCarousel(): boolean {
      return this.items.every((item: Product | ApiBrand) => item.type === ItemTypes.PRODUCT);
    },
    productItems(): Product[] {
      return this.items as Product[];
    },
    brandItems(): ApiBrand[] {
      return this.items as ApiBrand[];
    },
  },
  watch: {
    sectionId: {
      handler(sectionId, prevSectionId) {
        if (!sectionId || sectionId === prevSectionId) {
          return;
        }

        this.$emit('impression-id', sectionId);

        const ids = this.items.map((item, index) => ({
          id: item.id,
          position: index,
        }));

        this.shouldShow &&
          Analytics.track(
            new SectionCreatedEvent({
              id_section: sectionId,
              id_algo: this.algorithm,
              itemtype: this.items[0]?.type as snippetType,
              value_proposition: this.widgetProposition,
              section_type: ImpressionElement.recoWidget,
              id_brand: this.items[0]?.type === snippetType.brand ? ids : [],
              id_product: this.items[0]?.type === snippetType.product ? ids : [],
              position_component: this.positionInPage,
            })
          );
      },
      immediate: true,
    },
  },
  methods: {
    getLikeTrackingData(position: number, item: Product | ApiBrand): AdditionalLikeTrackingData {
      return {
        id_section: this.sectionId,
        component: this.$route?.name === 'carts' ? 'cart_oos_carousel' : null,
        itemtype: item?.type,
        position,
        ...(isProduct(item) && {
          id_brand: `${item.brand.id}`,
        }),
      };
    },
    productObserverChanged([entry, unobserve], item, index): void {
      if (entry.isIntersecting) {
        if (this.shouldTrackTileViews) {
          Analytics.trackMergeBuffer(
            new ViewTileEvent({
              userId: this.user?.id,
              event: EventName.ViewTile,
              page_path: this.$route.path,
              id_section: this.sectionId,
              section_type: ImpressionElement.recoWidget,
              id_algo: this.algorithm,
              itemtype: item.type,
              value_proposition: this.widgetProposition,
              id_brand: this.algorithm !== RecommendationAlgoType.productsYouMayLike ? { id: item.id, position: index } : null,
              id_product: this.algorithm === RecommendationAlgoType.productsYouMayLike ? { id: item.id, position: index } : null,
              position: null,
            }),
            trackMergeFn
          );
        }
        unobserve();
      }
    },
    productClicked(product: Product, index: number): void {
      this.$store.dispatch('tracking/setFromSectionId', this.sectionId);
      this.trackClick({
        productId: product.id,
        position: index,
        itemtype: snippetType.product,
        brandId: product.brand.id,
        action: 'product_clicked',
      });
    },
    brandClicked(brand: ApiBrand, index: number): void {
      this.trackClick({
        brandId: brand.id,
        position: index,
        itemtype: snippetType.brand,
        action: 'brand_clicked',
      });
    },
    trackClick({ brandId, productId, position, itemtype, action }: TrackClickItem): void {
      Analytics.track(
        new ClickEvent({
          id_section: this.sectionId,
          component: ImpressionElement.recoWidget.toString(),
          brandId,
          productId,
          position,
          itemtype,
          action,
          id_algo: this.algorithm,
        })
      );
    },
    trackQuickAddToCartProductAdded(trackingData: {
      productProperty: ProductProperty;
      brandProperty: BrandProperty;
      page: number;
      quantity: number;
    }): void {
      Analytics.track(
        new ProductQuickAddToCartEvent(
          trackingData.productProperty,
          trackingData.brandProperty,
          trackingData.page,
          trackingData.quantity,
          this.sectionId,
          this.algorithm as unknown as IdAlgo
        )
      );
    },
    handleBrandClicked(product: Product, index: number): void {
      this.trackClick({
        brandId: product.brand.id,
        productId: product.id,
        position: index,
        itemtype: snippetType.product,
        action: 'brand_clicked',
      });
    },
    handleCardProductAddedToCart(
      event: { option: { id: number; name: string }; quantity: number },
      product: Product,
      index: number
    ): void {
      const trackingData = {
        productProperty: createProductProperty({
          id: product.id,
          name: product.name as string,
          wholesalePrice: product.wholesale_price,
          position: index,
          option: event.option,
          uuid: product.uuid,
        }),
        brandProperty: createBrandProperty({
          id: product.brand.id,
          name: product.brand.name,
        }),
        page: 0,
        quantity: event.quantity * product.unit_multiplier,
      };
      this.trackQuickAddToCartProductAdded(trackingData);
    },
    handleLikeClicked(product: Product, index: number): void {
      this.trackClick({
        productId: product.id,
        brandId: product.brand.id,
        position: index,
        itemtype: snippetType.product,
        action: 'product_liked',
      });
    },
  },
});
</script>
